import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CommandDialog,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandShortcut,
} from '@/components/ui/command';
import { useGlobalSearch } from '@/hooks/useGlobalSearch';
import { useDebounce } from '@/hooks/useDebounce';
import {
  Building2,
  User,
  CalendarDays,
  FileText,
  Shield,
  Search,
  Command,
} from 'lucide-react';

interface GlobalCommandPaletteProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const iconMap: Record<string, React.ElementType> = {
  Building2,
  User,
  CalendarDays,
  FileText,
  Shield,
  Search,
};

export function GlobalCommandPalette({ open, onOpenChange }: GlobalCommandPaletteProps) {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearchQuery = useDebounce(searchQuery, 300);
  
  const { data: searchResults, isLoading } = useGlobalSearch(debouncedSearchQuery);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key === 'k') {
        event.preventDefault();
        onOpenChange(!open);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open, onOpenChange]);

  const handleSelect = (href: string) => {
    navigate(href);
    onOpenChange(false);
    setSearchQuery('');
  };

  // Group results by type
  const groupedResults = React.useMemo(() => {
    if (!searchResults) return {};

    return searchResults.reduce((acc, result) => {
      if (!acc[result.type]) {
        acc[result.type] = [];
      }
      acc[result.type].push(result);
      return acc;
    }, {} as Record<string, typeof searchResults>);
  }, [searchResults]);

  const getGroupHeader = (type: string) => {
    const headers = {
      church: 'Churches',
      profile: 'Members',
      event: 'Events', 
      report: 'Reports',
      audit: 'Audit Logs',
    };
    return headers[type as keyof typeof headers] || type;
  };

  const getGroupIcon = (type: string) => {
    const icons = {
      church: Building2,
      profile: User,
      event: CalendarDays,
      report: FileText,
      audit: Shield,
    };
    return icons[type as keyof typeof icons] || Search;
  };

  return (
    <CommandDialog open={open} onOpenChange={onOpenChange}>
      <CommandInput
        placeholder="Search churches, members, events, reports, and audit logs..."
        value={searchQuery}
        onValueChange={setSearchQuery}
      />
      <CommandList>
        {isLoading && (
          <div className="py-6 text-center text-sm text-muted-foreground">
            Searching...
          </div>
        )}
        
        {!isLoading && (!searchQuery || searchQuery.length < 2) && (
          <CommandEmpty>
            <div className="text-center py-6">
              <Search className="mx-auto h-8 w-8 text-muted-foreground mb-2" />
              <p className="text-sm text-muted-foreground mb-1">
                Start typing to search across the platform
              </p>
              <p className="text-xs text-muted-foreground">
                Search churches, members, events, reports, and audit logs
              </p>
            </div>
          </CommandEmpty>
        )}

        {!isLoading && searchQuery && searchQuery.length >= 2 && searchResults?.length === 0 && (
          <CommandEmpty>
            <div className="text-center py-6">
              <Search className="mx-auto h-8 w-8 text-muted-foreground mb-2" />
              <p className="text-sm text-muted-foreground">
                No results found for "{searchQuery}"
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                Try different keywords or check your spelling
              </p>
            </div>
          </CommandEmpty>
        )}

        {!isLoading && searchResults && searchResults.length > 0 && (
          <>
            {Object.entries(groupedResults).map(([type, results]) => {
              const IconComponent = getGroupIcon(type);
              return (
                <CommandGroup key={type} heading={getGroupHeader(type)}>
                  {results.map((result) => {
                    const ResultIcon = iconMap[result.icon || 'Search'] || Search;
                    return (
                      <CommandItem
                        key={result.id}
                        value={`${result.type}-${result.id}-${result.label}`}
                        onSelect={() => handleSelect(result.href)}
                        className="flex items-center gap-3"
                      >
                        <ResultIcon className="h-4 w-4 text-muted-foreground" />
                        <div className="flex-1 min-w-0">
                          <div className="font-medium truncate">{result.label}</div>
                          <div className="text-xs text-muted-foreground truncate">
                            {result.sublabel}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-muted-foreground/60 capitalize">
                            {type}
                          </span>
                          <CommandShortcut>↵</CommandShortcut>
                        </div>
                      </CommandItem>
                    );
                  })}
                </CommandGroup>
              );
            })}
          </>
        )}
      </CommandList>
    </CommandDialog>
  );
}
